import {
  ActionConfirmationModal,
  Button,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateFirmPhone,
  useDeleteFirmPhone,
  useFirmPhones,
  useUpdateFirmPhone,
} from "@/lib/data/react-query/firm/firm-phones";
import { FirmPhoneSchema } from "@/lib/data/schemas/firm/FirmSchema";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type AvailablePhoneNumber, type FirmPhone } from "@/lib/types/Firm";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { toLower } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { type z } from "zod";
import { SearchPhoneNumbersModal } from "./SearchPhoneNumbersModal";

const FORM_ID = "add-or-edit-firm-phone-form";

const formSchema = FirmPhoneSchema.pick({
  label: true,
  number: true,
});

interface AddOrEditFirmPhoneModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  phone?: FirmPhone;
}

export function AddOrEditFirmPhoneModal({
  open,
  setOpen,
  phone,
}: AddOrEditFirmPhoneModalProps) {
  const { hasActionPermission } = useUser();
  const [friendlyName, setFriendlyName] = useState(phone?.friendlyName ?? "");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      label: phone?.label ?? "",
      number: phone?.number ?? "",
    },
  });

  const createFirmPhone = useCreateFirmPhone();
  const updateFirmPhone = useUpdateFirmPhone();
  const deleteFirmPhone = useDeleteFirmPhone();
  const { data: firmPhonesData } = useFirmPhones();
  const firmPhones = useMemo(
    () => firmPhonesData?.data ?? [],
    [firmPhonesData],
  );
  const isFormLoading =
    createFirmPhone.isPending ||
    updateFirmPhone.isPending ||
    deleteFirmPhone.isPending;

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (
      firmPhones.some(
        (firmPhone) =>
          toLower(firmPhone.label.trim()) === toLower(data.label.trim()) &&
          firmPhone.id !== phone?.id,
      )
    ) {
      form.setError("label", {
        message: "Phone label must be unique",
      });
      return;
    }
    if (phone) {
      updateFirmPhone.mutate(
        {
          label: data.label,
          id: phone.id,
          dateModified: phone.dateModified,
        },
        {
          onSuccess: () => {
            setOpen(false);
            form.reset();
            setFriendlyName("");
          },
        },
      );
    } else {
      createFirmPhone.mutate(data, {
        onSuccess: () => {
          setOpen(false);
          form.reset();
          setFriendlyName("");
        },
      });
    }
  };

  const handlePhoneSelect = (phone: AvailablePhoneNumber) => {
    setFriendlyName(phone.friendlyName);
    form.setValue("number", phone.number, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleDelete = () => {
    if (phone?.id) {
      deleteFirmPhone.mutate({
        id: phone.id,
      });
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      form.reset();
      setFriendlyName("");
    }
  }, [open, form, setFriendlyName]);

  const deleteButton =
    phone &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.firmPhoneNumber,
      ACTION_PERMISSION_VALUES.delete,
    ) ? (
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          isDirty={form.formState.isDirty}
          title={phone ? "Edit phone number" : "Add phone number"}
          description={
            phone
              ? "Update the phone number details below"
              : "Add a new phone number to your firm"
          }
          loading={isFormLoading}
          size="2xl"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {deleteButton}
              <div className="flex grow justify-end gap-x-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                <FormSubmitButton
                  submitFormId={FORM_ID}
                  loading={isFormLoading}
                  disabled={isFormLoading}
                  title={phone ? "Update" : "Create"}
                />
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="overflow-y-auto">
              <fieldset
                className={slideoverFieldsetClassName}
                disabled={
                  phone
                    ? !hasActionPermission(
                        ACTION_PERMISSION_KEYS.firmPhoneNumber,
                        ACTION_PERMISSION_VALUES.update,
                      )
                    : false
                }
              >
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <FormLabel>Phone number</FormLabel>
                    <FormField
                      {...form.register("number")}
                      control={form.control}
                      render={() => (
                        <FormItem>
                          <FormMessage className="text-right" />
                        </FormItem>
                      )}
                    />
                  </div>
                  {form.watch("number") ? (
                    <div className="bg-accent/40 space-y-3 rounded-lg border p-3">
                      <div className="min-w-0">
                        <div className="truncate font-medium">
                          {friendlyName || "Selected Number"}
                        </div>
                        <div className="text-muted-foreground text-sm">
                          {form.watch("number")}
                        </div>
                      </div>
                      {!phone ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setIsSearchOpen(true);
                          }}
                        >
                          Change phone number
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <Button
                      className="w-full"
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsSearchOpen(true);
                      }}
                    >
                      Find a phone number
                    </Button>
                  )}
                </div>

                <TextFormField
                  register={form.register("label")}
                  label="Label"
                  placeholder="e.g. Main office, reception"
                />
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>
      <ActionConfirmationModal
        open={isDeleting}
        setOpen={setIsDeleting}
        title="Delete phone number"
        description="Are you sure you want to delete this phone number? This action cannot be undone."
        buttonText="Delete"
        afterActionFn={handleDelete}
      />
      <SearchPhoneNumbersModal
        open={isSearchOpen}
        setOpen={setIsSearchOpen}
        onSelect={handlePhoneSelect}
      />
    </>
  );
}
